import Decimal from "decimal.js"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { TAX_TYPES } from "src/constants/taxTypes"
import NumericInput from "src/main/PointOfSale/CurrentSalePanel/NumericInput"

import Button from "src/components/Button"
import Form from "src/components/Form"

const AddProductTaxForm = ({ onSave, onCancel }) => {
  const [name, setName] = useState("")
  const [taxType, setTaxType] = useState("")
  const [rate, setRate] = useState(0.01) // Stored as decimal (1%)
  const [errors, setErrors] = useState({})

  const validateForm = () => {
    const newErrors = {}

    if (!name.trim()) {
      newErrors.name = "Name is required"
    }

    if (!taxType) {
      newErrors.tax_type = "Tax type is required"
    }

    if (rate <= 0) {
      newErrors.rate = "Must be greater than 0"
    } else if (rate > 1) {
      newErrors.rate = "Rate cannot be more than 100%"
    } else {
      const [, decimals] = String(rate).split(".")
      if (decimals && decimals.length > 8) {
        newErrors.rate = "Rate can only be specified up to 6 decimal places"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleFormSubmit = () => {
    if (!validateForm()) {
      return
    }

    onSave({
      name,
      tax_type: taxType,
      rate,
    })

    // Reset form
    setName("")
    setTaxType("")
    setRate(0.01)
    setErrors({})
  }

  const handleCancel = () => {
    setName("")
    setTaxType("")
    setRate(0.01)
    setErrors({})
    onCancel()
  }

  // Display rate as percentage (multiply by 100)
  const displayRate = new Decimal(rate).mul(100).toNumber()

  return (
    <div className="bg-gray-50 mt-2 space-y-3 rounded border border-gray-200 p-4">
      <div className="space-y-3">
        <div>
          <Form.Label htmlFor="product-tax-name" required>
            Tax Name
          </Form.Label>
          <Form.TextField
            id="product-tax-name"
            name="product-tax-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Fuel Tax"
            hasErrors={!!errors.name}
          />
          {errors.name && <Form.Error>{errors.name}</Form.Error>}
        </div>

        <div>
          <Form.Label htmlFor="product-tax-type" required>
            Tax Type
          </Form.Label>
          <Form.Select
            id="product-tax-type"
            name="product-tax-type"
            value={taxType}
            onChange={(e) => setTaxType(e.target.value)}
            hasErrors={!!errors.tax_type}
          >
            <option value="" disabled={true}>
              Select tax type
            </option>
            {TAX_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Form.Select>
          {errors.tax_type && <Form.Error>{errors.tax_type}</Form.Error>}
        </div>

        <div>
          <Form.Label htmlFor="product-tax-rate" required>
            Rate
          </Form.Label>
          <NumericInput
            id="product-tax-rate"
            value={displayRate}
            onChange={(updatedValue) => {
              // Store as decimal (divide by 100)
              const decimalValue = new Decimal(updatedValue).div(100).toNumber()
              setRate(decimalValue)
            }}
            precision={4}
            icon="%"
            minValue={0.0001}
            maxValue={100}
            hasErrors={!!errors.rate}
          />
          {errors.rate && <Form.Error>{errors.rate}</Form.Error>}
        </div>

        <div className="flex space-x-2">
          <Button
            type="button"
            onClick={handleFormSubmit}
            variant="primary"
            small
          >
            Add Tax
          </Button>
          <Button type="button" onClick={handleCancel} variant="tertiary" small>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

AddProductTaxForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

export default AddProductTaxForm
